import { ArtifactsService, OpportunitiesService } from '@inference.engine/api-client/web';
import type { OpportunityDto } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/http-client';
import { downloadPdfBlob } from '@/shared/api/pdf-download.api';
import type { DurationTypeValue, InsightType, OpportunityCardItem } from '../model/types';

export const mapOpportunityDtoToCardItem = (dto: OpportunityDto): OpportunityCardItem =>
  ({
    ...dto.data,
    isFavorite: dto.isFavorite,
    durationType: dto.durationType ?? undefined,
    ideationData: dto.ideationData ?? undefined,
    insightType: dto.insightType ?? undefined,
  }) as OpportunityCardItem;

export type GetOpportunitiesParams = {
  workspaceId: string;
  topic?: string;
  skip?: number;
  take?: number;
  favoritesOnly?: boolean;
  sortBy?: 'created_at';
  sortOrder?: 'ASC' | 'DESC';
  excludeIds?: string[];
  dateStart?: string;
  dateEnd?: string;
  durationTypes?: DurationTypeValue[];
  insightType?: InsightType;
  market?: string;
  audience?: string;
  search?: string;
};

export const getOpportunities = async (params: GetOpportunitiesParams) => {
  const response = await OpportunitiesService.get({
    client: inferenceHttp,
    query: {
      workspaceId: params.workspaceId,
      topic: params.topic,
      skip: params.skip ?? 0,
      take: params.take ?? 20,
      favoritesOnly: params.favoritesOnly,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      excludeIds: params.excludeIds,
      dateStart: params.dateStart,
      dateEnd: params.dateEnd,
      durationTypes: params.durationTypes,
      insightType: params.insightType,
      market: params.market,
      audience: params.audience,
      search: params.search,
    },
  });

  if (!response.data) {
    throw new Error('No data returned from opportunities API');
  }

  return response.data;
};

export type OpportunitiesStats = {
  total: number;
  newCount: number;
  countByType: Partial<Record<DurationTypeValue, number>>;
};

/**
 * Aggregate KPI stats for the Opportunities Room, computed server-side.
 * `since` is the caller's "last visited" baseline used to derive `newCount`.
 */
export const getOpportunitiesStats = async (params: {
  workspaceId: string;
  topic?: string;
  since?: string;
}): Promise<OpportunitiesStats> => {
  const response = await OpportunitiesService.getStats({
    client: inferenceHttp,
    query: {
      workspaceId: params.workspaceId,
      topic: params.topic,
      since: params.since,
    },
  });

  if (!response.data) {
    throw new Error('No data returned from opportunities stats API');
  }

  return response.data as OpportunitiesStats;
};

export const getOpportunityById = async (
  workspaceId: string,
  opportunityId: string,
): Promise<OpportunityCardItem> => {
  const response = await OpportunitiesService.getById({
    client: inferenceHttp,
    path: { opportunityId },
    query: { workspaceId },
  });

  if (!response.data) {
    throw new Error('No data returned from opportunity by-id API');
  }

  return mapOpportunityDtoToCardItem(response.data);
};

/**
 * Download an opportunity as a server-rendered PDF blob.
 *
 * Uses the raw axios instance (the generated service isn't built for this binary
 * endpoint) with `responseType: 'blob'`.
 */
export const downloadOpportunityPdf = async (
  workspaceId: string,
  opportunityId: string,
): Promise<Blob> =>
  // The opportunities endpoint still scopes the render by this query param.
  // eslint-disable-next-line i18next/no-literal-string -- API resource path segment
  downloadPdfBlob('opportunities', opportunityId, {
    // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; every caller toasts its own translated message
    label: 'Opportunity',
    params: { workspaceId },
  });

export const favoriteOpportunity = async (
  workspaceId: string,
  opportunityId: string,
): Promise<void> => {
  await OpportunitiesService.favorite({
    client: inferenceHttp,
    path: { opportunityId },
    query: { workspaceId },
  });
};

export const unfavoriteOpportunity = async (
  workspaceId: string,
  opportunityId: string,
): Promise<void> => {
  await OpportunitiesService.unfavorite({
    client: inferenceHttp,
    path: { opportunityId },
    query: { workspaceId },
  });
};

/** Saves an opportunity to the workspace as a rich artifact and returns its
 *  artifactId. The backend derives all artifact fields server-side (and stores a
 *  self-contained snapshot) from the opportunity id; the call is idempotent. */
export const saveOpportunityArtifact = async (
  workspaceId: string,
  opportunity: OpportunityCardItem,
): Promise<string> => {
  const response = await OpportunitiesService.saveAsArtifact({
    client: inferenceHttp,
    path: { opportunityId: opportunity.opportunityId },
    query: { workspaceId },
  });

  if (!response.data) {
    throw new Error('Failed to create opportunity artifact');
  }

  return response.data.id;
};

/** Reads the self-contained opportunity snapshot stored on a saved artifact, for
 *  rendering the rich card in the project Library without the live opportunity. */
export const getSavedOpportunitySnapshot = async (
  workspaceId: string,
  artifactId: string,
): Promise<OpportunityCardItem> => {
  const response = await ArtifactsService.getWorkspaceArtifactById({
    client: inferenceHttp,
    path: { workspaceId, artifactId },
  });

  if (!response.data) {
    throw new Error('No data returned from workspace artifact API');
  }

  return response.data.data as OpportunityCardItem;
};
